import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { itinerariesApi } from 'services/apiDigitalTicket/itineraries';

// Helpers

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import { listLinesRequest } from 'store/ducks/lines/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';
import ENVIRONMENTS from 'constants/enviroments';
import { CHANNEL } from 'constants/ticketSales';

import * as ItineraryActions from './actions';
import { ItinerariesTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof ItineraryActions.listItinerariesRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando itinerários' }),
      );
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(itinerariesApi.list, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.listItinerariesSuccess({
          itinerariesList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - listItineraries - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.listItinerariesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listByWeekdays({
  payload,
}: ReturnType<typeof ItineraryActions.listItinerariesByWeekdaysRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando itinerários' }),
      );
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(itinerariesApi.listByWeekday, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.listItinerariesByWeekdaysSuccess({
          itinerariesByWeekdaysList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - listItineraries - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.listItinerariesByWeekdaysFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof ItineraryActions.getItineraryRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando itinerário' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(itinerariesApi.get, {
      params: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.getItinerarySuccess({ itineraryData: response.data }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('lines - getItinerary - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.getItineraryFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
}: ReturnType<typeof ItineraryActions.filterItinerariesRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(itinerariesApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.filterItinerariesSuccess({
          itinerariesList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - filterItineraries - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.filterItinerariesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof ItineraryActions.createItineraryRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando itinerário' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(itinerariesApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.createItinerarySuccess({
          itineraryData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Itinerário criado com sucesso',
        }),
      );
      yield put(push(PATH.ITINERARIES.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - createItinerary - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.createItineraryFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof ItineraryActions.updateItineraryRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando itinerário' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(itinerariesApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.updateItinerarySuccess({
          itineraryData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Itinerário alterado com sucesso',
        }),
      );
      yield window.location.reload();
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - updateItinerary - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.updateItineraryFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof ItineraryActions.deleteItineraryRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo itinerário' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(itinerariesApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Itinerário excluído com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(ItineraryActions.deleteItinerarySuccess());

      if (window.location.pathname === PATH.ITINERARIES.BASE_URL) {
        yield put(
          listLinesRequest({
            body: { _includes: 'linestop,lot,benefits,sections' },
          }),
        );

        return;
      }

      yield put(push(PATH.ITINERARIES.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - removeItinerary - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.deleteItineraryFailure());
    yield put(DialogsActions.setClose());
  }
}

function* resume({
  payload,
}: ReturnType<typeof ItineraryActions.resumeItineraryRequest>): any {
  try {
    const { showLoading, body, isSecondOpem } = payload;

    if (showLoading) {
      if (isSecondOpem) {
        yield put(
          DialogsActions.setSecondOpenLoading({
            title: 'Carregando Viagens...',
          }),
        );
      } else {
        yield put(
          DialogsActions.setOpenLoading({ title: 'Carregando Viagens...' }),
        );
      }
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(itinerariesApi.resume, {
      data: {
        transportOperatorId: transportOperator.id,
        ...body,
        channel: CHANNEL,
      },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.resumeItinerarySuccess({
          itineraryResumeList: response.data,
        }),
      );
      if (showLoading) {
        if (isSecondOpem) {
          yield put(DialogsActions.setSecondClose());
        } else {
          yield put(DialogsActions.setClose());
        }
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - resumeItinerary - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.resumeItineraryFailure());
    yield put(DialogsActions.setClose());
  }
}

function* stopDepartures({
  payload,
}: ReturnType<typeof ItineraryActions.stopDeparturesRequest>): any {
  try {
    const { body } = payload;
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(itinerariesApi.stopDepartures, {
      params: {
        transportOperatorId: transportOperator?.id,
        ...body,
      },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.stopDeparturesSuccess({
          stopDepartures: response.data,
        }),
      );
    }
  } catch (error) {
    console.error('itinerary - stopDepartures - error', error);
    yield put(ItineraryActions.stopDeparturesFailure());
  }
}

function* listTripCapacity({
  payload,
}: ReturnType<
  typeof ItineraryActions.resumeItineraryTripCapacityRequest
>): any {
  try {
    const response = yield call(itinerariesApi.getTripCapacity, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.resumeItineraryTripCapacitySuccess({
          itineraryResumeTripCapacityList: response.data,
        }),
      );
    }
  } catch (error) {
    // const internalCode: string =
    //   error.response && error.response.data.internalCode;
    // const message = errorMessagersList(internalCode);

    console.error('itinerary - resumeItineraryTripCapacity - error', error);
    // yield put(
    //   SnackbarsActions.setOpen({
    //     open: true,
    //     type: 'error',
    //     message,
    //   }),
    // );
    yield put(ItineraryActions.listItinerariesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* pedestrianTokens({
  payload,
  meta,
}: ReturnType<typeof ItineraryActions.pedestrianTokensRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));
    const { body } = payload;

    const response = yield call(itinerariesApi.pedestrianTokens, {
      data: { ...body },
    });

    if (response.status === 200) {
      yield put(ItineraryActions.pedestrianTokensSuccess());
      yield put(DialogsActions.setClose());
    }
    yield meta.promise.resolve(response);
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - pedestrianTokens - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.pedestrianTokensFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getBpePdf({
  payload,
  meta,
}: ReturnType<typeof ItineraryActions.getBpePdfRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));
    const { body } = payload;

    const response = yield call(itinerariesApi.bpeDabpePdf, {
      params: { ...body },
    });

    if (response.status === 200) {
      yield put(ItineraryActions.getBpePdfSuccess());
      yield put(DialogsActions.setClose());
    }
    yield meta.promise.resolve(response);
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - getBpePdf - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.getBpePdfFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* vehicleTokens({
  payload,
  meta,
}: ReturnType<typeof ItineraryActions.vehicleTokensRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));
    const { body } = payload;

    const response = yield call(itinerariesApi.vehicleTokens, {
      data: { ...body },
    });

    if (response.status === 200) {
      yield put(ItineraryActions.vehicleTokensSuccess());
      yield put(DialogsActions.setClose());
    }
    yield meta.promise.resolve(response);
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - vehicleTokens - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.vehicleTokensFailure());
    yield put(DialogsActions.setClose());
  }
}

function* rescheduleToken({
  payload,
  meta,
}: ReturnType<typeof ItineraryActions.rescheduleTokenRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));
    const { body } = payload;

    const response = yield call(itinerariesApi.rescheduleToken, {
      data: { ...body },
    });

    if (response.status === 200) {
      yield put(ItineraryActions.rescheduleTokenSuccess());
      yield put(DialogsActions.setClose());
    }
    yield meta.promise.resolve(response);
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - rescheduleToken - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.rescheduleTokenFailure());
    yield put(DialogsActions.setClose());
  }
}

function* seatPreReserve({
  payload,
  meta,
}: ReturnType<typeof ItineraryActions.seatPreReserveRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));
    const response = yield call(itinerariesApi.seatPreReserve, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(ItineraryActions.seatPreReserveSuccess());

      yield put(DialogsActions.setClose());
    }

    yield meta.promise.resolve(response);
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - seatPreReserve - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.seatPreReserveFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.resolve(error.response);
  }
}

function* filterVehicleLicense({
  payload,
}: ReturnType<typeof ItineraryActions.filterVehicleLicenseRequest>): any {
  try {
    const response = yield call(itinerariesApi.getVehicleLicense, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.filterVehicleLicenseSuccess({
          vehicleLicense: response.data,
          license: (payload as { license: string }).license,
        }),
      );
    }
  } catch (error) {
    console.error('itinerary - filterVehicleLicense - error', error);

    yield put(ItineraryActions.filterVehicleLicenseFailure());
    yield put(DialogsActions.setClose());
  }
}

function* tokenList({
  payload,
}: ReturnType<typeof ItineraryActions.tokenListRequest>): any {
  try {
    const { body } = payload;
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando Passagens' }));

    const response = yield call(itinerariesApi.tokenList, {
      params: { ...body },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.tokenListSuccess({
          tokenList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - tokenList - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.tokenListFailure());
    yield put(DialogsActions.setClose());
  }
}
function* userTransactionList({
  payload,
}: ReturnType<typeof ItineraryActions.userTransactionListRequest>): any {
  try {
    const { body } = payload;
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando Passagens' }));

    const response = yield call(itinerariesApi.userTransactionList, {
      params: { ...body, transportOperatorId: ENVIRONMENTS.OPERATOR_ID },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.userTransactionListSuccess({
          userTransactionList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - userTransactionList - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.userTransactionListFailure());
    yield put(DialogsActions.setClose());
  }
}

function* bpeList({
  payload,
}: ReturnType<typeof ItineraryActions.bpeListRequest>): any {
  try {
    const { body } = payload;
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando lista...' }));

    const response = yield call(itinerariesApi.appUserListBpe, {
      params: {
        ...body,
        // transportOperatorId: ENVIRONMENTS.OPERATOR_ID,
        transportOperatorId: '1208fc08-2019-4690-a296-fa22adea430d',
      },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.bpeListSuccess({
          bpeList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - bpeList - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.bpeListFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get3DsToken({
  payload,
}: ReturnType<typeof ItineraryActions.get3DsTokenRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));

    const response = yield call(itinerariesApi.get3DsToken, {
      data: {
        transportOperatorId: ENVIRONMENTS.OPERATOR_ID,
      },
    });

    if (response.status === 200) {
      yield put(
        ItineraryActions.get3DsTokenSuccess({
          token: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - get3DsToken - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.get3DsTokenFailure());
    yield put(DialogsActions.setClose());
  }
}

function* cancelBookedToken({
  payload,
}: ReturnType<typeof ItineraryActions.cancelBookedTokenRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Efetuando operação...' }),
    );
    const response = yield call(itinerariesApi.cancelBookedToken, {
      data: payload.body,
    });

    if (response.status === 200) {
      yield put(ItineraryActions.cancelBookedTokenSuccess());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Passagem cancelada com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('itinerary - cancelBookedToken - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(ItineraryActions.cancelBookedTokenFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const itinerariesSagas = [
  takeLatest(ItinerariesTypes.LIST_ITINERARIES_REQUEST, list),
  takeLatest(
    ItinerariesTypes.LIST_ITINERARIES_BY_WEEKDAYS_REQUEST,
    listByWeekdays,
  ),
  takeLatest(ItinerariesTypes.GET_ITINERARY_REQUEST, get),
  takeLatest(ItinerariesTypes.FILTER_ITINERARIES_REQUEST, filter),
  takeLatest(ItinerariesTypes.CREATE_ITINERARY_REQUEST, create),
  takeLatest(ItinerariesTypes.UPDATE_ITINERARY_REQUEST, update),
  takeLatest(ItinerariesTypes.DELETE_ITINERARY_REQUEST, remove),
  takeLatest(ItinerariesTypes.RESUME_ITINERARY_REQUEST, resume),
  takeLatest(ItinerariesTypes.STOP_DEPARTURES_REQUEST, stopDepartures),
  takeLatest(
    ItinerariesTypes.RESUME_ITINERARY_TRIP_CAPACITY_REQUEST,
    listTripCapacity,
  ),
  takeLatest(ItinerariesTypes.PEDESTRIAN_TOKENS_REQUEST, pedestrianTokens),
  takeLatest(ItinerariesTypes.VEHICLE_TOKENS_REQUEST, vehicleTokens),
  takeLatest(ItinerariesTypes.RESCHEDULE_TOKEN_REQUEST, rescheduleToken),
  takeLatest(ItinerariesTypes.SEAT_PRE_RESERVE_REQUEST, seatPreReserve),
  takeLatest(
    ItinerariesTypes.FILTER_VEHIVLE_LICENSE_REQUEST,
    filterVehicleLicense,
  ),
  takeLatest(ItinerariesTypes.TOKEN_LIST_REQUEST, tokenList),
  takeLatest(
    ItinerariesTypes.USER_TRASACTION_LIST_REQUEST,
    userTransactionList,
  ),
  takeLatest(ItinerariesTypes.CANCEL_BOOKED_TOKEN_REQUEST, cancelBookedToken),
  takeLatest(ItinerariesTypes.BPE_LIST_REQUEST, bpeList),
  takeLatest(ItinerariesTypes.GET_BPE_PDF_REQUEST, getBpePdf),

  takeLatest(ItinerariesTypes.GET_3DS_TOKEN_REQUEST, get3DsToken),
];
